import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchAgreementCashPosition } from '../services/agreements';
import { toScaled } from '../utils/invoiceSelection';
import { agreementKeys } from './agreementKeys';
import type { AgreementCashPosition } from '../types/agreements.types';

export const CLAIM_SETTLE_INTERVAL_MS = 2_000;
export const CLAIM_SETTLE_DEADLINE_MS = 20_000;

export interface CashPositionSettle {
    readBaseline: () => number | null;
    settleFrom: (baseline: number | null) => void;
    /** A claim poll is running: the held figure may not yet show the claim. */
    settling: boolean;
}

/** ledger-service posts a claim from its invoice.submitted consumer, after the submit responds.
 *  Fetches outside the query so the Invoicing tab never sees the poll as a balance reload. */
export function useCashPositionSettle(agreementId: string): CashPositionSettle {
    const queryClient = useQueryClient();
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const deadline = useRef<ReturnType<typeof setTimeout> | null>(null);
    const generation = useRef(0);
    const [settling, setSettling] = useState(false);

    const stop = useCallback(() => {
        generation.current += 1;
        setSettling(false);
        if (timer.current !== null) clearTimeout(timer.current);
        if (deadline.current !== null) clearTimeout(deadline.current);
        timer.current = null;
        deadline.current = null;
    }, []);

    useEffect(() => stop, [agreementId, stop]);

    const readBaseline = useCallback(() => {
        const held = queryClient.getQueryData<AgreementCashPosition>(agreementKeys.cashPosition(agreementId));
        return held ? toScaled(held.cashPosition) : null;
    }, [agreementId, queryClient]);

    const settleFrom = useCallback((baseline: number | null) => {
        stop();
        if (!agreementId || baseline === null || readBaseline() !== baseline) return;

        const armed = generation.current;
        const queryKey = agreementKeys.cashPosition(agreementId);
        deadline.current = setTimeout(stop, CLAIM_SETTLE_DEADLINE_MS);
        const poll = () => {
            timer.current = setTimeout(() => {
                fetchAgreementCashPosition(agreementId).then(
                    (fresh) => {
                        if (generation.current !== armed) return;
                        if (toScaled(fresh.cashPosition) !== baseline) {
                            stop();
                            queryClient.setQueryData(queryKey, fresh);
                        } else {
                            poll();
                        }
                    },
                    () => {
                        if (generation.current !== armed) return;
                        stop();
                        void queryClient.invalidateQueries({ queryKey });
                    },
                );
            }, CLAIM_SETTLE_INTERVAL_MS);
        };
        setSettling(true);
        poll();
    }, [agreementId, queryClient, readBaseline, stop]);

    return { readBaseline, settleFrom, settling };
}
